import React, { useState, useEffect, useMemo } from "react";
import { Modal, Input, Table, Tooltip } from "antd";
import { DownloadOutlined, FormOutlined, MailOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import UiButton from "../uiButton";
import { formatPhoneNumber } from "../../utils/appUtils";
import "./style.scss";

const isValidEmailList = (str) => {
    if (!str || !str.trim()) return false;
    const emails = str.split(/[,;]/).map(e => e.trim()).filter(Boolean);
    if (emails.length === 0) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.every(e => emailRegex.test(e));
};

const SubmerchantModal = ({
    open,
    onClose,
    onConfirm,
    selectedRecords = [],
    isLoading = false,
    toEmailAddress,
    ccEmailAddress
}) => {
    const configResults = useSelector((state) => state?.configAction?.configData);
    const [submerchantData, setSubmerchantData] = useState({});
    const [toEmail, setToEmail] = useState("");
    const [ccEmail, setCcEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [ccEmailError, setCcEmailError] = useState("");

    const resolvedToEmails = useMemo(() => {
        if (Array.isArray(toEmailAddress) && toEmailAddress.length > 0) {
            return toEmailAddress.map((item) => item?.value || item?.key || item).filter(Boolean).join(", ");
        }
        const toConfig = configResults?.find((detail) => detail?.configId === "advLabelToAddress");
        if (Array.isArray(toConfig?.configuration) && toConfig.configuration.length > 0) {
            return toConfig.configuration.filter(Boolean).join(", ");
        }
        return toConfig?.defaultValue || "";
    }, [toEmailAddress, configResults]);

    const resolvedCcEmails = useMemo(() => {
        if (Array.isArray(ccEmailAddress) && ccEmailAddress.length > 0) {
            return ccEmailAddress.map((item) => item?.value || item?.key || item).filter(Boolean).join(", ");
        }
        const ccConfig = configResults?.find((detail) => detail?.configId === "advLabelCcAddress");
        if (Array.isArray(ccConfig?.configuration) && ccConfig.configuration.length > 0) {
            return ccConfig.configuration.filter(Boolean).join(", ");
        }
        return ccConfig?.defaultValue || "";
    }, [ccEmailAddress, configResults]);

    // Initialize or reset when open state or selectedRecords change
    useEffect(() => {
        if (open) {
            const initial = {};
            (selectedRecords || []).forEach(record => {
                const key = record?.lawyerId || record?.id || record?.requestId || record?.lawyerNo;
                if (key) {
                    initial[key] = {
                        submerchantId: record?.submerchantId || ""
                    };
                }
            });
            setSubmerchantData(initial);
            setToEmail(resolvedToEmails);
            setCcEmail(resolvedCcEmails);
            setEmailError("");
            setCcEmailError("");
        }
    }, [open, selectedRecords, resolvedToEmails, resolvedCcEmails]);

    const handleFieldChange = (key, field, value) => {
        setSubmerchantData(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: value
            }
        }));
    };


    const handleSubmit = () => {
        let hasError = false;

        if (!toEmail.trim()) {
            setEmailError("Please enter To email ID");
            hasError = true;
        } else if (!isValidEmailList(toEmail)) {
            setEmailError("Please enter valid email address(es)");
            hasError = true;
        } else {
            setEmailError("");
        }

        if (ccEmail.trim() && !isValidEmailList(ccEmail)) {
            setCcEmailError("Please enter valid CC email address(es)");
            hasError = true;
        } else {
            setCcEmailError("");
        }

        if (hasError) return;

        onConfirm && onConfirm(submerchantData, {
            fromEmail: "merchant@mlawyer.in",
            toEmail: toEmail.trim(),
            ccEmail: ccEmail.trim()
        });
    };

    const tableData = useMemo(() => {
        return (selectedRecords || []).map((record, index) => {
            const key = record?.lawyerId || record?.id || record?.requestId || record?.lawyerNo || String(index);

            const advocateId = record?.lawyerNo || record?.lawyerId || record?.barCouncilId || record?.id || record?.requestId || "Nil";

            const profile = record?.lawyerDetails;
            const firstName = profile?.firstName || record?.firstName || "";
            const middleName = profile?.middleName || record?.middleName || "";
            const lastName = profile?.lastName || record?.lastName || "";
            let advocateName = `${firstName} ${middleName} ${lastName}`.trim();
            if (!advocateName) {
                advocateName = record?.lawyerName || record?.name || "Nil";
            }

            const rawPhone = profile?.mobileNumber || record?.mobileNumber || record?.cellNumber || record?.phone || "";
            const cellNumber = rawPhone ? formatPhoneNumber(rawPhone) : "Nil";

            return {
                key,
                sNo: index + 1,
                advocateId,
                advocateName,
                cellNumber,
                rawRecord: record
            };
        });
    }, [selectedRecords]);

    const columns = [
        {
            title: "S.No",
            dataIndex: "sNo",
            key: "sNo",
            width: 65,
            align: "center",
        },
        {
            title: "Advocate ID",
            dataIndex: "advocateId",
            key: "advocateId",
            width: 150,
            render: (text) => (
                <span className="advocate-id-badge">{text}</span>
            )
        },
        {
            title: "Advocate Name",
            dataIndex: "advocateName",
            key: "advocateName",
            width: 200,
            render: (text) => (
                <Tooltip title={text}>
                    <span className="advocate-name-text">{text}</span>
                </Tooltip>
            )
        },
        {
            title: "Cell Number",
            dataIndex: "cellNumber",
            key: "cellNumber",
            width: 140,
            render: (text) => (
                <span className="cell-number-text">{text}</span>
            )
        },
        {
            title: (
                <span className="table-header-input">
                    Submerchant ID
                </span>
            ),
            key: "submerchantId",
            width: 200,
            render: (_, record) => (
                <Input
                    placeholder="Enter Submerchant ID"
                    value={submerchantData[record.key]?.submerchantId || ""}
                    onChange={(event) => handleFieldChange(record.key, "submerchantId", event.target.value)}
                    className="submerchant-table-input"
                />
            )
        }
    ];

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={840}
            className="submerchant-modal"
            centered
            destroyOnClose
        >
            <div className="submerchant-modal-wrapper">
                <div className="submerchant-modal-header">
                    <div className="modal-title-row">
                        <div className="modal-icon-badge">
                            <FormOutlined />
                        </div>
                        <div>
                            <h3 className="modal-title">Submerchant & Email Details for Excel Report</h3>
                            <p className="modal-subtitle">
                                From: <strong>merchant@mlawyer.in</strong> &bull; Enter Submerchant ID, To Email, and CC Email to download report and open send option.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="email-inputs-container">
                    <div className="from-email-badge-row">
                        <span className="from-email-badge">
                            <MailOutlined className="from-email-icon" />
                            <span className="from-email-label">From Email:</span>
                            <span className="from-email-address">merchant@mlawyer.in</span>
                        </span>
                    </div>
                    <div className="email-inputs-row">
                        <div className="email-input-field">
                            <label className="email-input-label">
                                <MailOutlined className="email-label-icon" />
                                <span>To Email ID</span>
                                <span className="required-star">*</span>
                            </label>
                            <Input
                                placeholder="Enter To email ID (e.g. merchant@bank.com)"
                                value={toEmail}
                                onChange={(event) => {
                                    setToEmail(event.target.value);
                                    if (emailError) setEmailError("");
                                }}
                                status={emailError ? "error" : ""}
                                prefix={<MailOutlined style={{ color: "#9ca3af" }} />}
                                className="email-input-box"
                            />
                            {emailError && <span className="email-error-msg">{emailError}</span>}
                        </div>

                        <div className="email-input-field">
                            <label className="email-input-label">
                                <MailOutlined className="email-label-icon" />
                                <span>CC Email ID</span>
                                <span className="optional-badge">Optional</span>
                            </label>
                            <Input
                                placeholder="Enter CC email ID (e.g. team@bank.com)"
                                value={ccEmail}
                                onChange={(event) => {
                                    setCcEmail(event.target.value);
                                    if (ccEmailError) setCcEmailError("");
                                }}
                                status={ccEmailError ? "error" : ""}
                                prefix={<MailOutlined style={{ color: "#9ca3af" }} />}
                                className="email-input-box"
                            />
                            {ccEmailError && <span className="email-error-msg">{ccEmailError}</span>}
                        </div>
                    </div>
                </div>


                <div className="submerchant-table-container">
                    <Table
                        columns={columns}
                        dataSource={tableData}
                        pagination={false}
                        scroll={{ y: 320 }}
                        size="middle"
                        rowKey="key"
                        className="submerchant-table"
                    />
                </div>

                <div className="submerchant-modal-footer">
                    <div className="selected-count-badge">
                        Selected Advocates: <strong>{selectedRecords.length}</strong>
                    </div>
                    <div className="footer-actions">
                        <UiButton
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </UiButton>
                        <UiButton
                            type="primary"
                            onClick={handleSubmit}
                            loading={isLoading}
                            disabled={isLoading}
                        >
                            <DownloadOutlined /> Download Excel & Send
                        </UiButton>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default SubmerchantModal;
